<script lang="ts">
	import type { HistoryIssue, SnapshotDiff } from '#lib/history/types';
	import { i18n, t } from '#lib/i18n/state.svelte';
	import { scoreTone } from '#lib/ui/labels';

	interface Props {
		diff: SnapshotDiff;
		onclose: () => void;
	}
	let { diff, onclose }: Props = $props();

	const arrow = $derived(diff.scoreDelta > 0 ? '↑' : diff.scoreDelta < 0 ? '↓' : '→');
	const tone = $derived(
		diff.scoreDelta > 0 ? 'var(--ok)' : diff.scoreDelta < 0 ? 'var(--critical)' : 'var(--muted)'
	);

	function title(issue: HistoryIssue): string {
		return i18n.lang === 'en' ? issue.titleEn : issue.titleTr;
	}

	function where(issue: HistoryIssue): string {
		if (issue.page !== undefined) return t('common.page', { n: issue.page });
		if (issue.line !== undefined) return t('common.line', { n: issue.line });
		return '';
	}

	function categoryName(key: string): string {
		return t(`category.short.${key}`);
	}
</script>

<div class="overlay">
	<div
		class="modal"
		role="dialog"
		tabindex="-1"
		aria-modal="true"
		aria-label={t('diff.title')}
	>
		<header class="head">
			<div>
				<p class="eyebrow">{t('diff.eyebrow')}</p>
				<h2>{t('diff.title')}</h2>
				<p class="sub">{diff.v1.fileName} → {diff.v2.fileName}</p>
			</div>
			<button class="btn btn-ghost btn-sm" onclick={onclose} aria-label={t('common.close')}>✕</button>
		</header>

		<div class="banner">
			<div class="ver">
				<span class="vlabel">{t('diff.previous')}</span>
				<span class="vscore" style:color={scoreTone(diff.v1.score)}>{diff.v1.score}<span>{diff.v1.grade}</span></span>
			</div>
			<div class="delta" style:color={tone}>
				<span class="arrow">{arrow}</span>
				<span>{diff.scoreDelta > 0 ? '+' : ''}{diff.scoreDelta}</span>
			</div>
			<div class="ver">
				<span class="vlabel">{t('diff.current')}</span>
				<span class="vscore" style:color={scoreTone(diff.v2.score)}>{diff.v2.score}<span>{diff.v2.grade}</span></span>
			</div>
		</div>

		<section>
			<h3>{t('diff.categories')}</h3>
			<table>
				<thead>
					<tr>
						<th>{t('diff.category')}</th>
						<th>{t('diff.previous')}</th>
						<th>{t('diff.current')}</th>
						<th>{t('diff.change')}</th>
					</tr>
				</thead>
				<tbody>
					{#each diff.categories as cat (cat.category)}
						<tr>
							<td>{categoryName(cat.category)}</td>
							<td class="num">{cat.v1 ?? '—'}</td>
							<td class="num">{cat.v2 ?? '—'}</td>
							<td class="num" class:up={(cat.delta ?? 0) > 0} class:down={(cat.delta ?? 0) < 0}>
								{cat.delta === undefined ? '—' : `${cat.delta > 0 ? '+' : ''}${cat.delta}`}
								{#if cat.delta}<span class="small-arrow">{cat.delta > 0 ? '↑' : '↓'}</span>{/if}
							</td>
						</tr>
					{/each}
				</tbody>
			</table>
		</section>

		<div class="cols">
			<section>
				<h3 class="fixed">✅ {t('diff.fixed', { n: diff.fixed.length })}</h3>
				{#if diff.fixed.length === 0}<p class="empty">{t('diff.none')}</p>{/if}
				<ul>
					{#each diff.fixed as issue, i (issue.code + i)}
						<li><span class="code">{issue.code}</span><span>{title(issue)}</span>{#if where(issue)}<span class="loc">{where(issue)}</span>{/if}</li>
					{/each}
				</ul>
			</section>
			<section>
				<h3 class="added">⚠️ {t('diff.added', { n: diff.added.length })}</h3>
				{#if diff.added.length === 0}<p class="empty">{t('diff.none')}</p>{/if}
				<ul>
					{#each diff.added as issue, i (issue.code + i)}
						<li><span class="code">{issue.code}</span><span>{title(issue)}</span>{#if where(issue)}<span class="loc">{where(issue)}</span>{/if}</li>
					{/each}
				</ul>
			</section>
			<section>
				<h3>➡️ {t('diff.ongoing', { n: diff.ongoing.length })}</h3>
				{#if diff.ongoing.length === 0}<p class="empty">{t('diff.none')}</p>{/if}
				<ul>
					{#each diff.ongoing as issue, i (issue.code + i)}
						<li><span class="code">{issue.code}</span><span>{title(issue)}</span>{#if where(issue)}<span class="loc">{where(issue)}</span>{/if}</li>
					{/each}
				</ul>
			</section>
		</div>
	</div>
</div>

<style>
	.overlay {
		position: fixed;
		inset: 0;
		background: rgb(28 25 23 / 0.42);
		display: grid;
		place-items: center;
		padding: 1rem;
		z-index: 60;
	}
	.modal {
		width: min(58rem, 100%);
		max-height: min(46rem, 92vh);
		overflow: auto;
		background: var(--paper);
		border: 1px solid var(--line);
		border-radius: var(--radius);
		padding: 1.2rem;
		box-shadow: 0 24px 60px rgb(0 0 0 / 0.2);
	}
	.head {
		display: flex;
		justify-content: space-between;
		gap: 1rem;
	}
	.eyebrow {
		margin: 0 0 0.2rem;
		font-size: 0.68rem;
		text-transform: uppercase;
		letter-spacing: 0.1em;
		color: var(--muted);
		font-weight: 650;
	}
	h2 {
		margin: 0;
		font-size: 1.15rem;
	}
	.sub {
		margin: 0.15rem 0 0;
		color: var(--muted);
		font-size: 0.8rem;
		overflow-wrap: anywhere;
	}
	.banner {
		display: grid;
		grid-template-columns: 1fr auto 1fr;
		gap: 1rem;
		align-items: center;
		margin: 1rem 0 0.2rem;
		padding: 0.9rem 1rem;
		background: var(--paper-2);
		border: 1px solid var(--line);
		border-radius: 12px;
	}
	.ver {
		display: flex;
		flex-direction: column;
		gap: 0.1rem;
	}
	.ver:last-child {
		text-align: right;
	}
	.vlabel {
		font-size: 0.7rem;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: var(--muted);
	}
	.vscore {
		font-family: var(--font-display);
		font-size: 2rem;
		line-height: 1;
	}
	.vscore span {
		font-family: var(--font-ui);
		font-size: 0.8rem;
		margin-left: 0.2rem;
	}
	.delta {
		display: flex;
		align-items: center;
		gap: 0.2rem;
		font-weight: 700;
		font-size: 1.1rem;
	}
	.arrow {
		font-size: 1.6rem;
	}
	h3 {
		font-family: var(--font-ui);
		font-size: 0.85rem;
		margin: 1.2rem 0 0.4rem;
	}
	h3.fixed {
		color: var(--ok);
	}
	h3.added {
		color: var(--warning);
	}
	table {
		width: 100%;
		border-collapse: collapse;
		font-size: 0.82rem;
	}
	th,
	td {
		text-align: left;
		padding: 0.4rem 0.5rem;
		border-bottom: 1px solid var(--line);
	}
	th {
		font-size: 0.7rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--muted);
	}
	.num {
		text-align: right;
		font-variant-numeric: tabular-nums;
	}
	.up {
		color: var(--ok);
	}
	.down {
		color: var(--critical);
	}
	.small-arrow {
		margin-left: 0.15rem;
	}
	.cols {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 1rem;
	}
	ul {
		list-style: none;
		padding: 0;
		margin: 0;
		display: grid;
		gap: 0.35rem;
	}
	li {
		display: grid;
		gap: 0.1rem;
		padding: 0.4rem 0.5rem;
		border: 1px solid var(--line);
		border-radius: 8px;
		background: #fff;
		font-size: 0.78rem;
	}
	.code {
		font-family: var(--font-mono, monospace);
		font-size: 0.68rem;
		color: var(--muted);
	}
	.loc {
		font-size: 0.68rem;
		color: var(--muted);
	}
	.empty {
		margin: 0;
		color: var(--muted);
		font-size: 0.8rem;
	}
	@media (max-width: 720px) {
		.cols {
			grid-template-columns: 1fr;
		}
		.modal {
			padding: 1rem;
		}
	}
</style>
